// API service - Real backend integration only

import { apiClient } from '../auth/services/api-client'
import { mapMasterProductSearchResponse, mapMasterProductDetail, mapRawProductDetail } from './catalog-mappers'
import type { SearchQueryInfo } from './catalog-mappers'
import type { BranchStockListing } from '../utils/branchPriority'
import type {
  User,
  TenantSettings,
  MasterProduct,
  RecentPurchase,
  Branch,
  SearchSuggestion,
  Favourite,
  FavouriteList,
  FavouriteTargetType,
  RecentlyPurchasedTile,
  RemovedFavourite,
  BestListing,
  StockStatus,
  Category,
} from '../types'

interface ApiOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
}

// Per-dimension access mode toggle on a role. allow_all treats deny rows as
// exceptions; deny_all treats allow rows as the whitelist.
export type RoleAccessMode = 'allow_all' | 'deny_all'

// Row action for entries in the three access lists.
export type RoleAccessAction = 'allow' | 'deny'

export interface RoleCategoryAccessEntry {
  id: number
  name: string
  action: RoleAccessAction
}

export interface RoleSupplierAccessEntry {
  id: number
  name: string
  action: RoleAccessAction
}

export interface RoleBranchAccessEntry {
  id: string
  name: string
  branch_code: string
  supplier: { id: number; name: string }
  action: RoleAccessAction
}

// Role default configuration (ROLE-007). One entry per configurable base role.
// The three *_access_mode fields drive whether the corresponding list is
// consulted as a deny-list (allow_all mode) or an allow-list (deny_all mode).
export interface RoleDefaultConfig {
  slug: string
  name: string
  has_tenant_override: boolean
  basket_value_limit: string | null
  weekly_order_limit: string | null
  category_access_mode: RoleAccessMode
  supplier_access_mode: RoleAccessMode
  branch_access_mode: RoleAccessMode
  categories?: RoleCategoryAccessEntry[]
  suppliers?: RoleSupplierAccessEntry[]
  branch_access?: RoleBranchAccessEntry[]
}

// Weekly spend tracker payload (APR-012). When has_cap is false the role has
// no weekly cap and no tracker is shown; the amount fields are then absent.
export interface WeeklySpend {
  has_cap: boolean
  weekly_cap?: string
  spent?: string
  remaining?: string
}

// Approval preview (checkout). Predicts, per branch group, whether the order
// would be held for an approver or sent straight to the supplier. Advisory —
// create_order makes the authoritative decision inside its transaction.
export interface ApprovalPreviewGroup {
  key: string
  requires_approval: boolean
  reasons: string[]
  total: string
}

export interface ApprovalPreview {
  groups: ApprovalPreviewGroup[]
  any_direct_to_supplier: boolean
}

/** A trade counter the engineer is standing at (DEV-51045). */
export interface BranchAtPosition {
  branchId: string
  branchName: string
  supplierId: string
  supplierName: string
  distanceMiles: number
}

/** Wire shape of {@link BranchAtPosition}, before camel-casing. */
interface BranchAtPositionResponse {
  branch_id: string
  branch_name: string
  supplier_id: string
  supplier_name: string
  distance_miles: number
}

/**
 * Core API call function - calls Django backend only
 */
async function apiCall<T>(
  endpoint: string,
  options: ApiOptions = {}
): Promise<T> {
  const method = (options.method || 'GET').toUpperCase()
  switch (method) {
    case 'GET':
      return apiClient.get<T>(endpoint, { signal: options.signal })
    case 'POST':
      return apiClient.post<T>(endpoint, options.body)
    case 'PATCH':
      return apiClient.patch<T>(endpoint, options.body)
    case 'DELETE':
      return apiClient.delete<T>(endpoint)
    default:
      throw new Error(`Unsupported API method: ${method}`)
  }
}

// ---------------------------------------------------------------------
// Favourites / recently-purchased mappers (DEV-46921)
//
// Backend returns snake_case; frontend types are camelCase. Kept here
// alongside the endpoint methods since these mappers aren't shared with
// other services.
// ---------------------------------------------------------------------

function mapBestListing(raw: Record<string, unknown> | null | undefined): BestListing {
  const r = (raw || {}) as Record<string, unknown>
  const targetType =
    (r.target_type as FavouriteTargetType) ||
    (r.master_product_id != null ? 'master' : 'raw')
  return {
    targetType,
    masterProductId: r.master_product_id == null ? null : Number(r.master_product_id),
    rawProductId: r.raw_product_id == null ? null : Number(r.raw_product_id),
    displayName: (r.display_name as string | null) ?? null,
    imageUrl: (r.image_url as string | null) ?? null,
    mpn: (r.mpn as string | null) ?? null,
    supplierId: r.supplier_id == null ? null : Number(r.supplier_id),
    supplierName: (r.supplier_name as string | null) ?? null,
    branchId: (r.branch_id as string | null) ?? null,
    branchName: (r.branch_name as string | null) ?? null,
    price: r.price == null ? null : Number(r.price),
    distanceMiles: r.distance_miles == null ? null : Number(r.distance_miles),
    stockStatus: ((r.stock_status as StockStatus) || 'checking'),
    categoryL2Id: r.category_l2_id == null ? null : Number(r.category_l2_id),
    categoryL2Name: (r.category_l2_name as string | null) ?? null,
  }
}

function mapFavourite(raw: Record<string, unknown>): Favourite {
  const targetType =
    (raw.target_type as FavouriteTargetType) ||
    (raw.master_product_id != null ? 'master' : 'raw')
  return {
    id: String(raw.id),
    targetType,
    masterProductId: raw.master_product_id == null ? null : Number(raw.master_product_id),
    rawProductId: raw.raw_product_id == null ? null : Number(raw.raw_product_id),
    createdAt: String(raw.created_at ?? ''),
    listing: raw.listing
      ? mapBestListing(raw.listing as Record<string, unknown>)
      : null,
  }
}

function mapFavouriteList(raw: Record<string, unknown>): FavouriteList {
  return {
    id: String(raw.id),
    name: String(raw.display_name ?? ''),
    favouriteIds: Array.isArray(raw.favourite_ids) ? raw.favourite_ids.map(String) : [],
    createdAt: String(raw.created_at ?? ''),
    updatedAt: String(raw.updated_at ?? ''),
  }
}

// Location query string shared by the endpoints that resolve a tile for
// the caller's current postcode.
function locationQuery(location?: { postcode?: string; latitude?: number; longitude?: number }): string {
  const params = new URLSearchParams()
  if (location?.postcode) params.append('postcode', location.postcode)
  if (location?.latitude != null) params.append('latitude', String(location.latitude))
  if (location?.longitude != null) params.append('longitude', String(location.longitude))
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

function mapRecentlyPurchasedTile(raw: Record<string, unknown>): RecentlyPurchasedTile {
  return {
    orderItemId: Number(raw.order_item_id),
    orderId: String(raw.order_id),
    purchasedAt: String(raw.purchased_at ?? ''),
    listing: mapBestListing(raw.listing as Record<string, unknown>),
  }
}

/**
 * API methods - all call real backend
 */
export const api = {
  // ===== USER ENDPOINTS =====

  getCurrentUser: (): Promise<User> => {
    return apiCall('/api/users/me')
  },

  getFeaturebaseJwt: (): Promise<{ jwt: string }> => {
    return apiCall('/api/users/featurebase-jwt')
  },

  getUsers: (): Promise<User[]> => {
    return apiCall('/api/users/')
  },

  inviteUser: (data: { email: string; role: string; name: string }) => {
    return apiCall('/api/users/', { method: 'POST', body: data })
  },

  updateUser: (userId: string, data: Partial<User>) => {
    return apiCall(`/api/users/${userId}`, { method: 'PATCH', body: data })
  },

  removeUser: (userId: string) => {
    return apiCall(`/api/users/${userId}`, { method: 'DELETE' })
  },

  // ===== TENANT ENDPOINTS =====

  getTenantSettings: (): Promise<TenantSettings> => {
    return apiCall('/api/tenants/')
  },

  updateTenantBranding: (data: {
    logo_url?: string
    primary_color?: string
    nav_background_color?: string | null
    nav_text_color?: string | null
    button_text_color?: string | null
    name?: string
  }) => {
    return apiCall('/api/tenants/', { method: 'PATCH', body: data })
  },

  updateTenantSettings: (data: { name?: string; approval_threshold?: number }) => {
    return apiCall('/api/tenants/', { method: 'PATCH', body: data })
  },

  // ===== ROLE DEFAULTS ENDPOINTS =====

  getRoleDefaults: (): Promise<{ success: boolean; data: RoleDefaultConfig[] }> => {
    return apiCall('/api/roles/base-defaults/')
  },

  updateRoleDefaults: (slug: string, data: { basket_value_limit?: number | null; weekly_order_limit?: number | null }): Promise<{ success: boolean; data: RoleDefaultConfig }> => {
    return apiCall(`/api/roles/base-defaults/${slug}/`, { method: 'PATCH', body: data })
  },

  // ===== CATALOG ENDPOINTS =====

  // Get a single master product by ID
  getMasterProduct: async (
    id: string,
    postcode?: string,
    lat?: number,
    lon?: number,
  ): Promise<MasterProduct | null> => {
    try {
      // Location lets the backend flag suppliers with no branch in range so the
      // detail card can show "No nearby branch" reliably (not inferred from the
      // live-stock call).
      const params = new URLSearchParams()
      if (postcode) params.set('postcode', postcode)
      if (lat !== undefined) params.set('lat', String(lat))
      if (lon !== undefined) params.set('lon', String(lon))
      const qs = params.toString()
      const endpoint = `/api/catalog/master-products/${id}/${qs ? `?${qs}` : ''}`
      const response = await apiCall(endpoint)
      return mapMasterProductDetail(response)
    } catch (error) {
      console.error('Error fetching master product:', error)
      return null
    }
  },

  // Get a single supplier product by productId (raw product ID)
  getSupplierProduct: async (productId: string) => {
    try {
      const endpoint = `/api/catalog/products/${productId}/`
      const response = await apiCall(endpoint)
      return mapRawProductDetail(response)
    } catch (error) {
      console.error('Error fetching supplier product:', error)
      return null
    }
  },

  // Search master products. Stock-aware demotion is no longer requested
  // from the search endpoint — the frontend now hydrates per-card stock
  // pills via `checkSupplierStock` below. Postcode + lat/lon are still
  // sent so the backend can warm the stock cache in the background.
  searchMasterProducts: async (
    query: string,
    sortBy: 'price' | 'distance' | 'relevance' = 'price',
    page?: number,
    postcode?: string,
    lat?: number,
    lon?: number,
    categoryId?: number | null,
    noCorrection?: boolean,
    signal?: AbortSignal,
  ): Promise<{
    products: MasterProduct[]
    totalProducts: number
    hasNextPage: boolean
    hasPrevPage: boolean
    allMasterIds: string[]
    masterIdsBySupplier: Record<number, string[]>
    searchQuery: SearchQueryInfo
  }> => {
    const params = new URLSearchParams()
    params.append('q', query)
    params.append('sort', sortBy)
    if (page) params.append('page', page.toString())
    if (postcode) params.append('postcode', postcode)
    if (lat !== undefined) params.append('lat', lat.toString())
    if (lon !== undefined) params.append('lon', lon.toString())
    // Scope the search to a category when the user picked a category
    // suggestion. The backend filters by the category subtree (incl. L3),
    // excluding sibling categories.
    if (categoryId !== undefined && categoryId !== null) {
      params.append('category_id', categoryId.toString())
    }
    // Did-you-mean opt-out (Epic 3). Set when the user clicks the
    // "Search instead for …" revert link so the literal typed query is
    // searched without phrase-suggester auto-correction.
    if (noCorrection) {
      params.append('no_correction', '1')
    }

    const endpoint = `/api/catalog/master-products/?${params.toString()}`
    const response = await apiCall(endpoint, { signal }) as { next: string | null; previous: string | null; [key: string]: any }
    const mapped = mapMasterProductSearchResponse(response)

    return {
      ...mapped,
      hasNextPage: !!response.next,
      hasPrevPage: !!response.previous,
    }
  },

  // Autocomplete for the search input. Returns typed items: product names,
  // MPNs, and category leaf names. Category items carry a categoryId so
  // selecting one can scope the search to that category. Fails silently —
  // returns [] on any error so the input never breaks when the suggestions
  // endpoint is unavailable.
  searchSuggestions: async (
    query: string,
    signal?: AbortSignal,
    limit?: number,
  ): Promise<SearchSuggestion[]> => {
    const trimmed = query.trim()
    if (trimmed.length < 2) return []

    const params = new URLSearchParams()
    params.append('q', trimmed)
    if (limit && limit > 0) params.append('limit', limit.toString())

    try {
      const endpoint = `/api/catalog/search-suggestions/?${params.toString()}`
      const response = await apiClient.get<{
        suggestions: Array<{
          text: string
          type: SearchSuggestion['type']
          category_id: number | null
          path?: string | null
        }>
      }>(endpoint, { signal })
      if (!Array.isArray(response?.suggestions)) return []
      return response.suggestions
        .filter((item) => item && typeof item.text === 'string')
        .map((item) => ({
          text: item.text,
          type: item.type ?? 'product',
          categoryId: item.category_id ?? null,
          path: item.path ?? null,
        }))
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error
      }
      return []
    }
  },

  // Per-supplier progressive stock check for search results. The Search
  // page fires one of these per supplier present in the rendered window,
  // accumulates per-master state, and renders an IN STOCK / OUT OF STOCK
  // / CHECKING pill from the combined result. Errors from any supplier
  // are returned per-master so they can be treated as "doesn't vote"
  // rather than poisoning the whole pill state.
  checkSupplierStock: async (
    supplierId: number,
    masterIds: Array<string | number>,
    postcode: string,
    lat?: number,
    lon?: number,
    signal?: AbortSignal,
    keyword?: string,
  ): Promise<{
    supplierId: number
    supplierName: string
    summary: { in_stock: number; backorder?: number; out_of_stock: number; no_listing: number; error: number; total: number }
    errors: string[]
    // 'backorder' = no stock nearby but this supplier can backorder the product
    // (checkbox on, product eligible, date available) — orderable, no pill.
    masterResults: Record<string, 'in_stock' | 'backorder' | 'out_of_stock' | 'no_listing' | 'error'>
    masterDistances: Record<string, number | null>
    // BM-001: stored selling price of the raw that voted in-stock, keyed by
    // master id. Non-null only for in_stock; may still be null on cache hits
    // written before the backend started emitting this field.
    masterPrices: Record<string, number | null>
    // DEV-50739 SP-003: keyed by master id — for in_stock, the nearest
    // branches holding stock; for backorder, the branches where the
    // backorder could be placed. Empty otherwise, and for backend cache
    // hits written before the field existed.
    masterBranches: Record<string, string[]>
    // DEV-50739: per in-stock / backorder master, per branch id, every
    // listing that can supply it from there (cheapest-first by stored
    // price) with counts and stored price. Empty otherwise.
    masterBranchStock: Record<string, Record<string, BranchStockListing[]>>
  }> => {
    const normalizedIds = masterIds
      .map((value) => (typeof value === 'string' ? parseInt(value, 10) : value))
      .filter((value) => Number.isFinite(value))
    const response = await apiClient.post<{
      supplier_id: number
      supplier_name?: string
      summary?: { in_stock: number; backorder?: number; out_of_stock: number; no_listing: number; error: number; total: number }
      errors?: string[]
      master_results: Record<string, 'in_stock' | 'backorder' | 'out_of_stock' | 'no_listing' | 'error'>
      master_distances?: Record<string, number | null>
      master_prices?: Record<string, number | null>
      master_branches?: Record<string, string[]>
      master_branch_stock?: Record<
        string,
        Record<
          string,
          Array<{
            raw_id: number | null
            branch: number | null
            depot: number | null
            price: number | null
            distance_miles?: number | null
            product_name?: string | null
            product_code?: string | null
            quantity_multiple?: number | null
            can_collect?: boolean | null
            can_deliver?: boolean | null
            collection_stock?: number | null
            delivery_stock?: number | null
            earliest_collection?: string | null
            earliest_delivery?: string | null
            pickup_depot_stock?: number | null
            delivery_depot_stock?: number | null
            pickup_depot_date?: string | null
            delivery_depot_date?: string | null
            backorder_allowed?: boolean | null
            backorder_collection_date?: string | null
            backorder_delivery_date?: string | null
            backorder_date_from_api?: boolean | null
          }>
        >
      >
    }>(
      '/api/catalog/master-products/stock-check/',
      {
        supplier_id: supplierId,
        master_ids: normalizedIds,
        postcode,
        lat,
        lon,
        keyword,
      },
      { signal },
    )
    const errors = Array.isArray(response.errors) ? response.errors : []
    const summary = response.summary ?? {
      in_stock: 0,
      out_of_stock: 0,
      no_listing: 0,
      error: 0,
      total: 0,
    }
    if (errors.length > 0) {
      console.warn(
        `[stock-check] supplier ${response.supplier_id} reported errors:`,
        errors,
      )
    }
    return {
      supplierId: response.supplier_id,
      supplierName: response.supplier_name ?? '',
      summary,
      errors,
      masterResults: response.master_results,
      masterDistances: response.master_distances ?? {},
      masterPrices: response.master_prices ?? {},
      masterBranches: response.master_branches ?? {},
      masterBranchStock: Object.fromEntries(
        Object.entries(response.master_branch_stock ?? {}).map(([masterId, perBranch]) => [
          masterId,
          Object.fromEntries(
            Object.entries(perBranch).map(([branchId, listings]) => [
              branchId,
              (Array.isArray(listings) ? listings : []).map((entry) => ({
                rawId: entry.raw_id,
                branch: entry.branch,
                depot: entry.depot,
                price: entry.price,
                distanceMi: entry.distance_miles ?? null,
                // A current response always carries the per-method figures
                // (null = none for that method);
                // an older one omits them entirely.
                hasMethodStock: 'collection_stock' in entry || 'delivery_stock' in entry,
                productName: entry.product_name ?? null,
                productCode: entry.product_code ?? null,
                quantityMultiple: entry.quantity_multiple ?? 1,
                // Unknown counts as served, so an older response never
                // hides a method the branch offers.
                canCollect: entry.can_collect !== false,
                canDeliver: entry.can_deliver !== false,
                collectionStock: entry.collection_stock ?? null,
                deliveryStock: entry.delivery_stock ?? null,
                earliestCollection: entry.earliest_collection ?? null,
                earliestDelivery: entry.earliest_delivery ?? null,
                pickupDepotStock: entry.pickup_depot_stock ?? null,
                deliveryDepotStock: entry.delivery_depot_stock ?? null,
                pickupDepotDate: entry.pickup_depot_date ?? null,
                deliveryDepotDate: entry.delivery_depot_date ?? null,
                backorderAllowed: entry.backorder_allowed === true,
                backorderCollectionDate: entry.backorder_collection_date ?? null,
                backorderDeliveryDate: entry.backorder_delivery_date ?? null,
                backorderDateFromApi: entry.backorder_date_from_api === true,
              })),
            ]),
          ),
        ]),
      ),
    }
  },

  // DEV-50739: which of the basket's branches the caller's role may still
  // order from (active branch + active supplier, neither denied). The
  // "Show first" row hides pills for any other branch. Never throws —
  // on failure it returns null so the row keeps every pill rather than
  // hiding legitimate ones.
  getBranchPriorityEligibility: async (
    branchIds: string[],
    signal?: AbortSignal,
  ): Promise<string[] | null> => {
    try {
      const response = await apiClient.post<{ allowed_branch_ids?: string[] }>(
        '/api/catalog/branch-priority/eligibility/',
        { branch_ids: branchIds },
        { signal },
      )
      return Array.isArray(response?.allowed_branch_ids)
        ? response.allowed_branch_ids.map(String)
        : null
    } catch {
      return null
    }
  },

  // DEV-51045: the collection branches the engineer is standing at, nearest
  // first. Already filtered by the backend for range, collection capability
  // and the caller's role. Never throws — on failure it returns an empty
  // list, so a lookup that fails just offers no branch rather than blocking
  // the engineer from setting their location.
  getBranchesAtPosition: async (
    latitude: number,
    longitude: number,
    signal?: AbortSignal,
  ): Promise<BranchAtPosition[]> => {
    try {
      const query = new URLSearchParams({
        latitude: String(latitude),
        longitude: String(longitude),
      })
      const response = await apiClient.get<{
        branches?: BranchAtPositionResponse[]
      }>(`/api/catalog/branch-priority/at-position/?${query.toString()}`, {
        signal,
      })
      if (!Array.isArray(response?.branches)) return []
      return response.branches.map((branch) => ({
        branchId: String(branch.branch_id),
        branchName: branch.branch_name,
        supplierId: String(branch.supplier_id),
        supplierName: branch.supplier_name,
        distanceMiles: branch.distance_miles,
      }))
    } catch {
      return []
    }
  },

  // Progressive per-supplier price refresh for search results. Mirrors
  // `checkSupplierStock` — Search.tsx fires one call per supplier in the
  // visible window; each hits the backend's stored-first pricing pipeline
  // (valid stored → serve from DB, expired → live API + write back). Cards
  // patch their displayed price once all relevant suppliers respond, so
  // the visible number is never higher than the initial paint just because
  // one supplier resolved slower than another.
  refreshSupplierPrices: async (
    supplierId: number,
    masterIds: Array<string | number>,
    signal?: AbortSignal,
  ): Promise<{
    supplierId: number
    supplierName: string
    masterPrices: Record<string, number | null>
    // DEV-50739: refreshed price per raw listing id (null = unavailable),
    // so a prioritised card can price the exact listing it names.
    rawPrices: Record<string, number | null>
    // Pack size each live price was quoted under, where the supplier reports
    // one (CEF); raws without one are absent.
    rawQuantityMultiples: Record<string, number>
  }> => {
    const normalizedIds = masterIds
      .map((value) => (typeof value === 'string' ? parseInt(value, 10) : value))
      .filter((value) => Number.isFinite(value))
    const response = await apiClient.post<{
      supplier_id: number
      supplier_name?: string
      master_prices: Record<string, number | null>
      raw_prices?: Record<string, number | null>
      raw_quantity_multiples?: Record<string, number>
    }>(
      '/api/catalog/master-products/refresh-prices/',
      {
        supplier_id: supplierId,
        master_ids: normalizedIds,
      },
      { signal },
    )
    return {
      supplierId: response.supplier_id,
      supplierName: response.supplier_name ?? '',
      masterPrices: response.master_prices ?? {},
      rawPrices: response.raw_prices ?? {},
      rawQuantityMultiples: response.raw_quantity_multiples ?? {},
    }
  },

  // Get recent purchases for quick re-order (legacy — kept for backwards
  // compatibility; the engineer Home tab uses `listRecentlyPurchased`
  // below which returns the same tile shape as favourites).
  getRecentPurchases: (): Promise<RecentPurchase[]> => {
    return apiCall('/api/catalog/recent-purchases')
  },

  // Categories the caller may browse (search page empty state). Returns a flat
  // list scoped by the caller's role; the frontend builds the tree from
  // parentId. Read-only — does not touch search.
  getCategories: async (): Promise<Category[]> => {
    const response = await apiCall<{
      categories: Array<{
        id: number
        name: string
        parent_id: number | null
        path: string
        depth: number
        child_count: number
      }>
    }>('/api/catalog/categories/browse/')
    return (response.categories || []).map((category) => ({
      id: category.id,
      name: category.name,
      parentId: category.parent_id ?? null,
      path: category.path,
      depth: category.depth,
      childCount: category.child_count ?? 0,
    }))
  },

  // ===== FAVOURITES ENDPOINTS (DEV-46921) =====

  // GET /api/favourites/
  // Returns every product the caller has favourited (master + raw
  // targets mixed, newest first), each resolved to a tile-ready best
  // listing for the caller's postcode.
  listFavourites: async (
    postcode?: string,
    latitude?: number,
    longitude?: number,
  ): Promise<Favourite[]> => {
    const params = new URLSearchParams()
    if (postcode) params.append('postcode', postcode)
    if (latitude != null) params.append('latitude', String(latitude))
    if (longitude != null) params.append('longitude', String(longitude))
    const qs = params.toString()
    const endpoint = `/api/favourites/${qs ? `?${qs}` : ''}`
    const response = await apiCall<{ results: Array<Record<string, unknown>>; count: number }>(endpoint)
    return (response.results || []).map(mapFavourite)
  },

  // GET /api/favourites/ids/
  // Lightweight lookup for hydrating heart-icon state across Search /
  // ProductDetail / SupplierProductDetail without paying the tile-
  // resolution cost. Returns two sets so both master pages AND
  // supplier-product pages can consistently show the right heart state.
  listFavouriteIds: async (): Promise<{
    masterProductIds: number[]
    rawProductIds: number[]
  }> => {
    const response = await apiCall<{
      master_product_ids: number[]
      raw_product_ids: number[]
    }>('/api/favourites/ids/')
    return {
      masterProductIds: response.master_product_ids || [],
      rawProductIds: response.raw_product_ids || [],
    }
  },

  // POST /api/favourites/  { master_product_id } OR { raw_product_id }
  // Exactly one target id per request. Idempotent — adding a product
  // that's already a favourite returns 200 with the existing row; a
  // new favourite returns 201.
  addFavourite: async (
    target: { type: FavouriteTargetType; id: number | string },
    location?: { postcode?: string; latitude?: number; longitude?: number },
  ): Promise<Favourite> => {
    const params = new URLSearchParams()
    if (location?.postcode) params.append('postcode', location.postcode)
    if (location?.latitude != null) params.append('latitude', String(location.latitude))
    if (location?.longitude != null) params.append('longitude', String(location.longitude))
    const qs = params.toString()
    const endpoint = `/api/favourites/${qs ? `?${qs}` : ''}`
    const body =
      target.type === 'master'
        ? { master_product_id: Number(target.id) }
        : { raw_product_id: Number(target.id) }
    const raw = await apiCall<Record<string, unknown>>(endpoint, {
      method: 'POST',
      body,
    })
    return mapFavourite(raw)
  },

  // DELETE /api/favourites/master/{master_product_id}/
  // DELETE /api/favourites/raw/{raw_product_id}/
  // Also takes the favourite out of every list (DEV-51010). Returns the
  // favourite id (for Undo) and the lists it left, or null when the
  // product wasn't a favourite (idempotent 204).
  removeFavourite: async (target: {
    type: FavouriteTargetType
    id: number | string
  }): Promise<RemovedFavourite | null> => {
    const response = await apiCall<{
      favourite_id: string
      removed_list_ids: string[]
    } | undefined>(`/api/favourites/${target.type}/${target.id}/`, {
      method: 'DELETE',
    })
    if (!response) return null
    return {
      favouriteId: String(response.favourite_id),
      removedListIds: (response.removed_list_ids || []).map(String),
    }
  },

  // POST /api/favourites/{favourite_id}/restore/
  // Undo after un-hearting: restores the favourite and the list
  // memberships that un-heart removed. Returns the favourite with its tile
  // resolved for the caller's postcode, like addFavourite.
  restoreFavourite: async (
    favouriteId: string,
    location?: { postcode?: string; latitude?: number; longitude?: number },
  ): Promise<Favourite> => {
    const raw = await apiCall<Record<string, unknown>>(
      `/api/favourites/${favouriteId}/restore/${locationQuery(location)}`,
      { method: 'POST' },
    )
    return mapFavourite(raw)
  },

  // ===== FAVOURITE LISTS (DEV-51010) =====
  // Named groups of favourites. Rule failures come back as
  // { error, code } with the copy the app shows inline (400 for an empty
  // or over-long name, 409 for a duplicate, 404 for a missing list).

  // GET /api/favourites/lists/ — every list, most recently changed first.
  listFavouriteLists: async (): Promise<FavouriteList[]> => {
    const response = await apiCall<{ results: Array<Record<string, unknown>>; count: number }>(
      '/api/favourites/lists/',
    )
    return (response.results || []).map(mapFavouriteList)
  },

  // POST /api/favourites/lists/ — `favouriteId` puts that favourite in the
  // new list (the Save to list sheet's "New list").
  createFavouriteList: async (name: string, favouriteId?: string): Promise<FavouriteList> => {
    const raw = await apiCall<Record<string, unknown>>('/api/favourites/lists/', {
      method: 'POST',
      body: favouriteId ? { display_name: name, favourite_id: favouriteId } : { display_name: name },
    })
    return mapFavouriteList(raw)
  },

  // PATCH /api/favourites/lists/{list_id}/
  renameFavouriteList: async (listId: string, name: string): Promise<FavouriteList> => {
    const raw = await apiCall<Record<string, unknown>>(`/api/favourites/lists/${listId}/`, {
      method: 'PATCH',
      body: { display_name: name },
    })
    return mapFavouriteList(raw)
  },

  // DELETE /api/favourites/lists/{list_id}/ — the items stay favourites.
  deleteFavouriteList: (listId: string): Promise<void> => {
    return apiCall(`/api/favourites/lists/${listId}/`, { method: 'DELETE' })
  },

  // POST /api/favourites/lists/{list_id}/items/ — idempotent.
  addToFavouriteList: async (listId: string, favouriteId: string): Promise<FavouriteList> => {
    const raw = await apiCall<Record<string, unknown>>(`/api/favourites/lists/${listId}/items/`, {
      method: 'POST',
      body: { favourite_id: favouriteId },
    })
    return mapFavouriteList(raw)
  },

  // DELETE /api/favourites/lists/{list_id}/items/{favourite_id}/ — removes
  // it from this list only; it stays a favourite. Idempotent.
  removeFromFavouriteList: async (listId: string, favouriteId: string): Promise<FavouriteList> => {
    const raw = await apiCall<Record<string, unknown>>(
      `/api/favourites/lists/${listId}/items/${favouriteId}/`,
      { method: 'DELETE' },
    )
    return mapFavouriteList(raw)
  },

  // GET /api/favourites/recently-purchased/
  // Up to 40 most-recent order items (no dedupe in v1), each with a
  // resolved tile listing for the caller's postcode.
  listRecentlyPurchased: async (
    postcode?: string,
    latitude?: number,
    longitude?: number,
  ): Promise<RecentlyPurchasedTile[]> => {
    const params = new URLSearchParams()
    if (postcode) params.append('postcode', postcode)
    if (latitude != null) params.append('latitude', String(latitude))
    if (longitude != null) params.append('longitude', String(longitude))
    const qs = params.toString()
    const endpoint = `/api/favourites/recently-purchased/${qs ? `?${qs}` : ''}`
    const response = await apiCall<{ results: Array<Record<string, unknown>>; count: number }>(endpoint)
    return (response.results || []).map(mapRecentlyPurchasedTile)
  },

  // Legacy search (for backwards compatibility)
  searchProducts: (query: string): Promise<MasterProduct[]> => {
    return apiCall(`/api/catalog/products?q=${query}`)
  },

  // Record that the user navigated past page 1 of a search result list.
  // Used as a passive "first page didn't satisfy" signal. Fire-and-forget;
  // errors are swallowed by the caller so failures never block navigation.
  trackSearchPageTurn: (data: { query: string; page_number: number }): Promise<{ success: boolean }> => {
    return apiCall('/api/catalog/search-events/page-turn/', { method: 'POST', body: data })
  },

  // Record the first product the user opened from a search result list,
  // along with its 1-indexed position and the page it was clicked on.
  // Frontend should only call this for the first click per (query, page)
  // so the position remains a meaningful "result depth" signal.
  trackSearchProductClick: (data: {
    query: string
    master_product_id: number
    position: number
    page_number: number
  }): Promise<{ success: boolean }> => {
    return apiCall('/api/catalog/search-events/product-click/', { method: 'POST', body: data })
  },

  // Submit explicit free-text feedback from the "Can't find what you're
  // looking for?" prompt below the search results.
  submitSearchFeedback: (data: {
    query: string
    feedback_text: string
  }): Promise<{ success: boolean }> => {
    return apiCall('/api/catalog/search-events/feedback/', { method: 'POST', body: data })
  },

  // ===== BRANCH ENDPOINTS =====

  // Get all branches with optional filters
  getBranches: (params?: {
    supplier_id?: number
    postcode?: string
    is_active?: boolean
    search?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.supplier_id) queryParams.append('supplier_id', params.supplier_id.toString())
    if (params?.postcode) queryParams.append('postcode', params.postcode)
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString())
    if (params?.search) queryParams.append('search', params.search)

    const endpoint = `/api/suppliers/branches/?${queryParams.toString()}`
    return apiCall(endpoint)
  },

  // Get single branch detail
  getBranch: (id: string): Promise<Branch> => {
    return apiCall<Branch>(`/api/suppliers/branches/${id}/`)
  },

  // ===== ORDER ENDPOINTS =====

  getOrders: () => {
    return apiCall('/api/orders/')
  },

  // Weekly spend vs the user's role cap (APR-012). has_cap=false → no tracker.
  getWeeklySpend: (): Promise<WeeklySpend> => {
    return apiCall('/api/orders/weekly-spend/')
  },

  // Would these checkout groups route for approval, or go straight to the
  // supplier? Runs the real approval rules server-side (no order is created).
  getApprovalPreview: (
    groups: Array<{ key: string; cart_item_ids: string[] }>,
  ): Promise<ApprovalPreview> => {
    return apiCall('/api/orders/approval-preview/', {
      method: 'POST',
      body: { groups },
    })
  },

  getOrder: (orderId: string): Promise<{
    id: string
    order_number: string
    status: string
    approval_reasons?: string[]
    approval_context?: Record<string, string>
    supplier_status: string | null
    total_amount: string
    fulfillment_type: string | null
    supplier_id: string | null
    supplier_name: string
    branch_id: string | null
    branch_name: string
    user_id: string | null
    placed_by_name: string
    placed_by_email: string | null
    placed_by_phone: string | null
    placed_by_role: string | null
    submission_attempts: number
    submission_error: string | null
    created_at: string
    updated_at: string
    // DEV-51011: booked date for the success page (AC9)
    expected_fulfillment_date: string | null
    requested_fulfillment_date: string | null
    supports_preferred_date: boolean
    booked_fulfillment_date?: string | null
    // Branch details for collection orders
    branch_address: string | null
    branch_phone: string | null
    postcode: string | null
    branch_details: {
      address: string
      postcode: string
      phone: string
    } | null
    items: Array<{
      id: string
      product_name: string
      quantity: number
      unit_price: string
      total_price: string
      product_snapshot: {
        name?: string
        image_url?: string
        [key: string]: unknown
      }
    }>
  }> => {
    return apiCall(`/api/orders/${orderId}/`)
  },

  getPendingApprovals: () => {
    return apiCall('/api/orders/pending-approvals')
  },

  // Ask another approver (by email) to review an order awaiting approval
  // (APR-002). The backend validates that the email belongs to an approver in
  // the same tenant and emails them an approval link.
  notifyApprover: (
    orderId: string,
    email: string
  ): Promise<{ success: boolean; message: string }> => {
    return apiCall(`/api/orders/${orderId}/notify-approver/`, {
      method: 'POST',
      body: { email },
    })
  },

  approveOrder: (orderId: string) => {
    return apiCall(`/api/orders/${orderId}/approve`, { method: 'POST' })
  },

  rejectOrder: (orderId: string, reason: string) => {
    return apiCall(`/api/orders/${orderId}/reject`, { method: 'POST', body: { reason } })
  },

  // ===== LIVE PRICING & STOCK ENDPOINTS =====

  // Get live prices for raw products (supplier products)
  // bypassCache: true for checkout page — asks the supplier every time.
  // live: true for the basket — ignores the supplier's price-cycle validity so
  // the price is current, while still reusing a recent answer so opening the
  // basket does not call the supplier on every render.
  getLivePrices: (
    rawProductIds: number[],
    bypassCache: boolean = false,
    live: boolean = false,
  ): Promise<{ prices: any[] }> => {
    const idsParam = rawProductIds.join(',')
    const cacheParam = bypassCache ? '&bypass_cache=true' : ''
    const liveParam = !bypassCache && live ? '&live=true' : ''
    const endpoint = `/api/suppliers/live-price/?raw_product_ids=${idsParam}${cacheParam}${liveParam}`
    return apiCall(endpoint)
  },

  // Get live stock for raw products (supplier products) - Master product page (nearest 5 branches)
  // Optional `supplierId` scopes the call to a single supplier. The detail
  // page uses this to fan out one parallel request per supplier so one
  // slow supplier's response doesn't block the others. Omitting the
  // param keeps the original all-suppliers behaviour.
  getLiveStock: (
    rawProductIds: (number | string)[],
    postcode?: string,
    lat?: number,
    lon?: number,
    supplierId?: number | string,
  ): Promise<{ stock: any[] }> => {
    const idsParam = rawProductIds.join(',')
    const postcodeParam = postcode ? `&postcode=${encodeURIComponent(postcode)}` : ''
    const latParam = lat !== undefined ? `&lat=${lat}` : ''
    const lonParam = lon !== undefined ? `&lon=${lon}` : ''
    const supplierParam = supplierId !== undefined ? `&supplier_id=${supplierId}` : ''
    const endpoint = `/api/suppliers/live-stock/?raw_product_ids=${idsParam}${postcodeParam}${latParam}${lonParam}${supplierParam}`
    return apiCall(endpoint)
  },

  // Get cart stock for specific branch + product combinations - Cart page (exact branches).
  // Optional per-item `quantity` (Order-on-Demand) makes the supplier return
  // availability dates for the requested amount rather than the qty-1 probe.
  getCartStock: (
    items: Array<{ raw_product_id: number | string; branch_id: string; quantity?: number }>,
    postcode?: string,
    lat?: number,
    lon?: number
  ): Promise<{ stock: any[]; restricted_items?: Array<{ raw_product_id: number | string; branch_id: string; restricted: boolean; reason: string; label: string }> }> => {
    const endpoint = '/api/suppliers/cart-stock/'
    return apiCall(endpoint, {
      method: 'POST',
      body: {
        items,
        postcode,
        lat,
        lon,
      },
    })
  },

  // Check stock levels for products. Pass `quantity` for a quantity-specific
  // probe (Order-on-Demand date re-check); the backend forces a live call for
  // qty > 1 so it never collides with the cached qty-1 entry.
  checkStock: (params: {
    supplier_id: number
    product_codes: string[]
    branch_id?: string
    postcode?: string
    quantity?: number
    bypass_cache?: boolean
  }) => {
    const endpoint = '/api/suppliers/stock/'
    return apiCall(endpoint, { method: 'POST', body: params })
  },

  // ===== ADDRESS LOOKUP =====

  /**
   * Lookup UK addresses by postcode using Ideal Postcodes API (via backend proxy)
   * @param postcode UK postcode to lookup
   * @returns List of addresses for the postcode
   */
  lookupAddress: (postcode: string): Promise<{
    success: boolean
    data?: {
      postcode: string
      addresses: Array<{
        line_1: string
        line_2?: string
        line_3?: string
        post_town: string
        postcode: string
        county?: string
        building_name?: string
        sub_building_name?: string
      }>
    }
    error?: {
      message: string
      code: string
    }
  }> => {
    const endpoint = `/api/address/lookup?postcode=${encodeURIComponent(postcode)}`
    return apiCall(endpoint)
  },

  // ===== ORDER ENDPOINTS =====

  /**
   * Create a new order from cart items
   * @param data Order creation data
   * @returns Created order details
   */
  createOrder: (data: {
    branch_id: string
    /** Branch the order is recorded ON when it differs from branch_id (same
     * supplier only). Set by checkout when the delivery postcode routes CPS /
     * Wolseley to a different fulfilment branch than the engineer's job
     * postcode (DEV-52059). Cart-item lookup and role checks still use
     * branch_id — only Order.branch changes. */
    order_branch_id?: string
    /** Exact cart-line ids for this order. When provided, the order is built
     * from these items (a delivery group can span original branches); the
     * order is placed on branch_id. Falls back to all cart items at branch_id
     * when omitted. */
    cart_item_ids?: string[]
    fulfillment_type: 'delivery' | 'collection'
    delivery_address: {
      line_1: string
      line_2?: string
      line_3?: string
      post_town: string
      postcode: string
    }
    job_number?: string
    reason?: string
    secondary_reason?: string
    van_stock_items?: string[]
    /** Earliest fulfilment date supplier reported at checkout time (ISO string). */
    expected_fulfillment_date?: string | null
    /**
     * Day the engineer chose in the checkout date sheet (YYYY-MM-DD, DEV-51011).
     * Null/omitted when they kept the earliest date.
     */
    requested_fulfillment_date?: string | null
    /**
     * Branch stock available at checkout, keyed by cart-line id (not
     * raw_product_id — a delivery order can hold two lines of the same product).
     * Stored as branch_stock_at_order so approvers can compare was-vs-now (AC2).
     */
    stock_at_order?: Record<string, number>
    /**
     * Per-item expected collection/delivery date at checkout, keyed by cart-line
     * id (ISO string). Stored as expected_date_at_order so approval can show a
     * per-item date change (APR-011 AC1).
     */
    dates_at_order?: Record<string, string>
    /**
     * Lines the checkout page showed as backorders, keyed by cart-line id:
     * units above branch + depot stock and the backorder date shown (ISO or
     * null). The backend rejects the order if any such line isn't allowed by
     * the supplier/product backorder policy, and records them on the order.
     */
    backorders_at_order?: Record<string, { quantity: number; date: string | null }>
    /**
     * Analytics join key for the checkout this order came from — minted
     * client-side on first entry to checkout (see lib/checkout-session.ts).
     * Persisted on the Order so the asynchronous server-side order events can
     * carry it too. Malformed values are stored as null by the backend.
     */
    checkout_id?: string
  }): Promise<{
    id: string
    order_number: string
    status: string
    supplier_order_id?: string
    total_amount: string
    submission_warning?: string
    can_retry?: boolean
    items: Array<{
      id: string
      product_name: string
      quantity: number
      unit_price: string
      total_price: string
      product_snapshot: Record<string, unknown>
    }>
  }> => {
    return apiCall('/api/orders/', { method: 'POST', body: data })
  },

  /**
   * Retry a failed order submission
   * @param orderId Order ID to retry
   * @returns Updated order details
   */
  retryOrder: (orderId: string): Promise<{
    id: string
    order_number: string
    status: string
    supplier_order_id?: string
    submission_warning?: string
    can_retry?: boolean
  }> => {
    return apiCall(`/api/orders/${orderId}/retry/`, { method: 'POST' })
  },
}
